import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminFetch } from '@/adminApi';
import { useAdmin } from '@/contexts/AdminContext';

interface Subscription {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  tier: string;
  status: string;
  start_date: string;
  end_date: string;
  billing_period: string;
  custom_price: string | null;
  base_price: string | null;
}

export default function AdminSubscriptionsPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Action modal
  const [actionModal, setActionModal] = useState<{ sub: Subscription; type: 'change-tier' | 'extend' } | null>(null);
  const [newTier, setNewTier] = useState('premium');
  const [additionalDays, setAdditionalDays] = useState(30);
  const [actionLoading, setActionLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout>>();
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3000);
  };

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, tierFilter, statusFilter]);

  const loadSubscriptions = useCallback(async () => {
    if (!isAuthenticated) { navigate('/admin'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(), limit: '50',
        ...(tierFilter && { tier: tierFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(debouncedSearch && { search: debouncedSearch }),
      });
      const data = await adminFetch<{ subscriptions: Subscription[]; total: number }>('GET', `/api/admin/subscriptions?${params}`);
      setSubscriptions(data.subscriptions);
      setTotal(data.total);
    } catch { setSubscriptions([]); }
    finally { setLoading(false); }
  }, [page, tierFilter, statusFilter, debouncedSearch, isAuthenticated, navigate]);

  useEffect(() => { loadSubscriptions(); }, [loadSubscriptions]);

  const openAction = (sub: Subscription, type: 'change-tier' | 'extend') => {
    setActionModal({ sub, type });
    setNewTier(sub.tier);
    setAdditionalDays(30);
  };

  const submitAction = async () => {
    if (!actionModal) return;
    setActionLoading(true);
    try {
      const { sub, type } = actionModal;
      const endpoint = type === 'change-tier' ? 'change-tier' : 'extend';
      const body = type === 'change-tier' ? { newTier } : { additionalDays };
      await adminFetch('PATCH', `/api/admin/subscriptions/${sub.id}/${endpoint}`, body);
      showToast(type === 'change-tier' ? `Тариф изменён на ${newTier}` : `Подписка продлена на ${additionalDays} дней`);
      setActionModal(null);
      loadSubscriptions();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally { setActionLoading(false); }
  };

  const totalPages = Math.ceil(total / 50);
  const tierBadge = (tier: string) => {
    const c: Record<string, string> = { base: 'bg-teal-600/30 text-teal-300', premium: 'bg-purple-600/30 text-purple-300', pro: 'bg-amber-600/30 text-amber-300' };
    return c[tier] || 'bg-slate-600 text-slate-200';
  };
  const statusBadge = (s: string) => {
    const c: Record<string, string> = { active: 'bg-green-600/30 text-green-300', cancelled: 'bg-red-600/30 text-red-300', expired: 'bg-amber-600/30 text-amber-300' };
    return c[s] || 'bg-slate-600 text-slate-200';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {toast && <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{toast.message}</div>}

      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-lg font-bold text-white hover:text-purple-400 transition">← Назад</Link>
          <h1 className="text-lg font-bold text-white">Подписки ({total})</h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по email/имени..."
            className="flex-1 min-w-48 px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value="">Все тарифы</option>
            <option value="base">Base</option>
            <option value="premium">Premium</option>
            <option value="pro">Pro</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value="">Все статусы</option>
            <option value="active">Активные</option>
            <option value="cancelled">Отменённые</option>
            <option value="expired">Истёкшие</option>
          </select>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div> : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Имя</th>
                      <th className="px-4 py-3 font-medium">Тариф</th>
                      <th className="px-4 py-3 font-medium">Статус</th>
                      <th className="px-4 py-3 font-medium">Период</th>
                      <th className="px-4 py-3 font-medium">Окончание</th>
                      <th className="px-4 py-3 font-medium">Цена</th>
                      <th className="px-4 py-3 font-medium">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.length === 0 ? (
                      <tr><td colSpan={8} className="px-6 py-8 text-center text-slate-500">Подписки не найдены</td></tr>
                    ) : subscriptions.map((sub) => (
                      <tr key={sub.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                        <td className="px-4 py-3"><Link to={`/admin/users/${sub.user_id}`} className="text-blue-400 hover:underline text-sm">{sub.email}</Link></td>
                        <td className="px-4 py-3 text-slate-300 text-sm">{sub.full_name || '—'}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${tierBadge(sub.tier)}`}>{sub.tier}</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge(sub.status)}`}>{sub.status}</span></td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{sub.billing_period}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{new Date(sub.end_date).toLocaleDateString('ru-RU')}</td>
                        <td className="px-4 py-3 text-white text-sm">{sub.custom_price || sub.base_price ? `${sub.custom_price || sub.base_price} ₽` : '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <button onClick={() => openAction(sub, 'change-tier')} className="text-xs px-2 py-1 bg-teal-600/20 text-teal-400 rounded hover:bg-teal-600/40 transition">Тариф</button>
                            <button onClick={() => openAction(sub, 'extend')} className="text-xs px-2 py-1 bg-amber-600/20 text-amber-400 rounded hover:bg-amber-600/40 transition">Продлить</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-sm text-slate-400">Страница {page} из {totalPages}</span>
                  <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition">←</button>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition">→</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-white mb-2">
              {actionModal.type === 'change-tier' ? 'Изменить тариф' : 'Продлить подписку'}
            </h2>
            <p className="text-slate-400 text-sm mb-5">{actionModal.sub.email} — текущий тариф: {actionModal.sub.tier}</p>
            {actionModal.type === 'change-tier' ? (
              <div className="mb-5">
                <label className="block text-sm text-slate-400 mb-1">Новый тариф</label>
                <select value={newTier} onChange={(e) => setNewTier(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="base">Base (290 ₽/мес)</option>
                  <option value="premium">Premium (690 ₽/мес)</option>
                  <option value="pro">Pro (990 ₽/мес)</option>
                </select>
              </div>
            ) : (
              <div className="mb-5">
                <label className="block text-sm text-slate-400 mb-1">Дней для продления</label>
                <input type="number" value={additionalDays} onChange={(e) => setAdditionalDays(parseInt(e.target.value) || 1)} min={1} max={365}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setActionModal(null)} className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition">Отмена</button>
              <button onClick={submitAction} disabled={actionLoading}
                className={`px-4 py-2 text-sm rounded-lg transition disabled:opacity-50 ${actionModal.type === 'change-tier' ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-amber-600 text-white hover:bg-amber-700'}`}>
                {actionLoading ? 'Выполняется...' : 'Применить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
